import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import type { FaqItem } from "../components/seo/FAQSection";
import { buildServiceSchema } from "../data/seo/structuredData";
import useManagedSupportPricing from "../hooks/useManagedSupportPricing";

const PRICE_PLACEHOLDER = "—";

const buildPlans = (agentPrice: string, supervisorPrice: string) => [
    {
        name: "Support Agent",
        price: agentPrice,
        cadence: "/agent /month",
        description: "A trained human agent who handles live chat, tickets, and email directly in your FloGPT inbox.",
        points: ["Live chat & ticket handling", "Email + omnichannel replies", "Works alongside your AI agent", "Scale seats up or down anytime"],
    },
    {
        name: "Supervisor",
        price: supervisorPrice,
        cadence: "/supervisor /month",
        description: "A support supervisor who oversees quality, handles escalations, and keeps your SLAs on track.",
        points: ["Quality assurance & coaching", "Escalation handling", "SLA & queue oversight", "Reporting on team performance"],
    },
];

const buildWorkflow = (agentPrice: string, supervisorPrice: string, providerLabel: string) => [
    { step: "1", title: "Request your seats", description: "From your dashboard, choose how many agents and supervisors you need." },
    {
        step: "2",
        title: `Pay securely via ${providerLabel}`,
        description: `A simple monthly subscription — ${agentPrice}/agent, ${supervisorPrice}/supervisor. Cancel anytime.`,
    },
    { step: "3", title: "Book your kickoff meeting", description: "We align on your product, support hours, tone, and tools before staff go live." },
    { step: "4", title: "Your team goes live", description: "We assign trained staff into your support inbox — they pick up right where the AI hands off." },
];

const buildFaqs = (agentPrice: string, supervisorPrice: string, providerLabel: string, billingSummary: string): FaqItem[] => [
    {
        question: "Who are the managed support agents?",
        answer: "They are trained customer-support professionals hired and managed by FloGPT. They work inside your FloGPT support inbox using your knowledge base, so responses stay on-brand and grounded in your content.",
    },
    {
        question: "How much does managed support cost?",
        answer: `Support agents are ${agentPrice}/month each and supervisors are ${supervisorPrice}/month each, billed monthly through ${providerLabel}. You can request one or many seats and change the count as your volume changes.`,
    },
    {
        question: "Is managed support available everywhere?",
        answer: `Managed customer support is available to organizations worldwide. Subscriptions are billed in ${billingSummary}, matching your organization's billing currency. Sign up, then request managed support from your dashboard to get started.`,
    },
    {
        question: "How does it work with my AI agents?",
        answer: "Your AI agents handle the front line and deflect tier-one questions. When a conversation escalates, your managed human staff pick it up in the same unified inbox — with the full transcript and contact identity already attached.",
    },
    {
        question: "How long does it take to get staffed?",
        answer: "After you pay and complete a short kickoff meeting, we assign trained staff to your organization and onboard them to your product and support playbook.",
    },
];

export default function ManagedSupportMarketingPage() {
    const { agentPrice, supervisorPrice, providerLabel, billingSummary } = useManagedSupportPricing();
    const agent = agentPrice ?? PRICE_PLACEHOLDER;
    const supervisor = supervisorPrice ?? PRICE_PLACEHOLDER;

    const plans = useMemo(() => buildPlans(agent, supervisor), [agent, supervisor]);
    const workflow = useMemo(() => buildWorkflow(agent, supervisor, providerLabel), [agent, supervisor, providerLabel]);
    const faqs = useMemo(
        () => buildFaqs(agent, supervisor, providerLabel, billingSummary),
        [agent, supervisor, providerLabel, billingSummary],
    );

    usePageSeo({
        title: "Managed Customer Support — Outsourced Human Agents & Supervisors | FloGPT",
        description: `Add trained human support agents and supervisors to your FloGPT inbox — hired, trained, and managed by us. Available worldwide and billed in ${billingSummary}.`,
        keywords:
            "managed customer support, outsourced support agents, outsourced customer service, support as a service, human support agents, dedicated support team",
        canonical: "/managed-support",
    });

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT Managed Customer Support",
                description: `Outsourced, fully-managed human customer-support agents and supervisors who work inside your FloGPT unified inbox alongside your AI agents. Agents ${agent}/month, supervisors ${supervisor}/month.`,
                path: "/managed-support",
                serviceType: "Managed / Outsourced Customer Support Staffing",
                features: [
                    `Trained human support agents (${agent}/month)`,
                    `Support supervisors (${supervisor}/month)`,
                    "Staff work inside your FloGPT unified inbox",
                    "AI-to-human handover with full transcript",
                    `Pay-as-you-grow monthly seats via ${providerLabel}`,
                    "Kickoff onboarding meeting included",
                ],
            }),
        [agent, supervisor, providerLabel],
    );

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            <main>
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="overview">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-orange-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-pink-500/15 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-6">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                    Managed Customer Support · Worldwide
                                </div>
                                <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.35rem] lg:leading-[1.05]">
                                    AI on the front line.{" "}
                                    <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">Real humans</span>{" "}
                                    when it matters.
                                </h1>
                                <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
                                    Don't want to recruit, train, and manage a support team? We'll staff it for you. Add
                                    trained human agents and supervisors who work directly inside your FloGPT inbox — hired
                                    and managed by us.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                    >
                                        Get Started Free
                                    </Link>
                                    <a
                                        href="#book-free-demo"
                                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Talk to Us
                                    </a>
                                </div>
                                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
                                    <span>· {agent}/agent per month</span>
                                    <span>· {supervisor}/supervisor per month</span>
                                    <span>· Available worldwide · {billingSummary}</span>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="relative"
                            >
                                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-orange-500/25 to-pink-500/25 blur-2xl" />
                                <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-3xl border border-slate-700/80 bg-slate-900/80 p-6 sm:p-8 shadow-2xl">
                                    {plans.map((p) => (
                                        <div key={p.name} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
                                            <div className="text-xs uppercase tracking-wider text-slate-400">{p.name}</div>
                                            <div className="mt-2 text-3xl font-bold text-white">
                                                {p.price}
                                                {/* Kept unbreakable so the longer "/supervisor /month" wraps as a
                                                    unit instead of stranding "/month" on its own line. */}
                                                <span className="whitespace-nowrap text-sm font-normal text-slate-400"> {p.cadence}</span>
                                            </div>
                                            <p className="mt-3 text-sm text-slate-400">{p.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* PLANS DETAIL */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Two roles, staffed and managed for you</h2>
                        <p className="mt-4 text-slate-300">Request exactly what you need — a single agent or a full team with supervisors.</p>
                    </div>
                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {plans.map((p) => (
                            <article key={p.name} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                                <div className="flex items-baseline justify-between">
                                    <h3 className="text-xl font-semibold text-white">{p.name}</h3>
                                    <div className="text-2xl font-bold text-white">
                                        {p.price}
                                        <span className="whitespace-nowrap text-sm font-normal text-slate-400"> {p.cadence}</span>
                                    </div>
                                </div>
                                <ul className="mt-5 space-y-3 text-slate-300">
                                    {p.points.map((pt) => (
                                        <li key={pt} className="flex items-start gap-3">
                                            <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-orange-400 to-pink-400" />
                                            <span>{pt}</span>
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </section>

                {/* WORKFLOW */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">How it works</h2>
                        <p className="mt-4 text-slate-300">From request to a live, staffed support team in a few simple steps.</p>
                    </div>
                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {workflow.map((s, idx) => (
                            <motion.article
                                key={s.step}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                viewport={{ once: true, amount: 0.2 }}
                                className="relative rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20"
                            >
                                <div className="absolute -top-3 left-6 inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-[12px] font-bold text-white shadow">
                                    {s.step}
                                </div>
                                <h3 className="mt-3 text-lg font-semibold text-white">{s.title}</h3>
                                <p className="mt-2 text-slate-300 leading-relaxed text-sm">{s.description}</p>
                            </motion.article>
                        ))}
                    </div>
                </section>

                <FAQSection
                    id="managed-support"
                    eyebrow="Managed Support · FAQ"
                    heading="Common questions about managed support"
                    items={faqs}
                />

                {/* CTA + BOOKING */}
                <section id="book-free-demo" className="mx-auto max-w-6xl px-6 lg:px-8 pb-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white">Ready to staff your support team?</h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Book a free consultation, or sign up and request managed support directly from your dashboard.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="mt-12 max-w-4xl mx-auto"
                    >
                        <ScheduleBookingForm
                            title="Talk to us about Managed Support"
                            subtitle="Tell us your support volume and hours — we'll recommend the right number of agents and supervisors"
                            submitLabel="Request a Consultation"
                        />
                    </motion.div>
                </section>
            </main>
            <Footer />

            <JsonLd id="service-managed-support" data={serviceSchema} />
        </div>
    );
}
